import { useEffect, useRef } from "react";

/**
 * V46 alarmlarını dashboard tarayıcısında zamanlanmış şekilde çalan hook.
 *
 * - Her 30 sn'de bir alarm listesini backend'den çeker (yeni/silinen yansısın).
 * - Her 20 sn'de bir vakti gelen alarmları kontrol eder.
 * - "times" tipi: HH:MM eşleşmesinde ve o gün için daha önce çalmadıysa çalar.
 * - "interval" tipi: son çalma zamanından N dakika geçtiyse çalar.
 * - Son çalma zamanları localStorage'a yazılır (yeniden yüklemeyi atlatır).
 * - Aynı sekmede de çift çalmayı önler (memory cache).
 *
 * Browser playback — V46 protokolü özel ses oynatmayı desteklemediği için
 * ses kaydı dashboard'da çalar; bu sebeple en az bir kullanıcı sayfayı
 * açık tutmalıdır.
 */
const API = "https://app.vivasmartcare.com/api";
const FETCH_INTERVAL_MS = 30 * 1000;
const CHECK_INTERVAL_MS = 20 * 1000;
const FALLBACK_BEEP_DATA_URL =
  // Kısa beep (varsayılan uyarı sesi, base64 wav)
  "data:audio/wav;base64,UklGRl4AAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YT4AAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA";

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const lsKey = (alarmId, suffix) =>
  `v46_alarm_fired_${alarmId}_${suffix}`;

const playAudio = (alarm) => {
  try {
    const src = alarm.audioData
      ? `data:${alarm.audioMime || "audio/webm"};base64,${alarm.audioData}`
      : FALLBACK_BEEP_DATA_URL;
    const a = new Audio(src);
    a.play().catch((err) => console.warn("Alarm sesi çalınamadı:", err));
    // Tarayıcı bildirimi (varsa)
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      try {
        new Notification("Alarm", {
          body: alarm.description || "Hatırlatıcı",
          tag: alarm._id,
        });
      } catch (_) {}
    }
  } catch (e) {
    console.error("Alarm çalma hatası:", e);
  }
};

const useV46AlarmScheduler = (organization) => {
  const alarmsRef = useRef([]);
  const memFiredRef = useRef(new Set()); // sekme içi tekrar engelle

  // Alarmları periyodik çek
  useEffect(() => {
    if (!organization) return;
    let cancelled = false;

    const fetchAlarms = async () => {
      try {
        const params = new URLSearchParams({ company: organization });
        const r = await fetch(`${API}/v46-alarms?${params}`);
        const data = await r.json();
        if (cancelled) return;
        alarmsRef.current = Array.isArray(data)
          ? data.filter((a) => a.enabled)
          : [];
      } catch (e) {
        console.error("V46 alarmları çekilirken hata:", e);
      }
    };

    fetchAlarms();
    const id = setInterval(fetchAlarms, FETCH_INTERVAL_MS);

    // Tarayıcı bildirim izni iste (sessizce başarısız olabilir)
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().catch(() => {});
    }

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [organization]);

  // Zamanı gelen alarmları çal
  useEffect(() => {
    if (!organization) return;

    const tick = () => {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const currentHHMM = `${hh}:${mm}`;
      const day = todayKey();

      for (const a of alarmsRef.current) {
        if (!a.enabled) continue;

        if (a.scheduleType === "times") {
          const list = Array.isArray(a.times) ? a.times : [];
          for (const t of list) {
            if (t !== currentHHMM) continue;
            const k = lsKey(a._id, `times_${day}_${t}`);
            const memK = `${a._id}|${day}|${t}`;
            if (memFiredRef.current.has(memK)) continue;
            try {
              if (localStorage.getItem(k)) {
                memFiredRef.current.add(memK);
                continue;
              }
            } catch (_) {}
            playAudio(a);
            memFiredRef.current.add(memK);
            try {
              localStorage.setItem(k, String(Date.now()));
            } catch (_) {}
          }
        } else if (a.scheduleType === "interval") {
          const intervalMs = Math.max(1, Number(a.intervalMinutes) || 60) * 60_000;
          const k = lsKey(a._id, "interval_last");
          const memK = `${a._id}|interval`;
          let last = 0;
          try {
            last = Number(localStorage.getItem(k)) || 0;
          } catch (_) {}
          // İlk fire: alarm oluşturulduktan en az 1 periyot sonra başlatmak
          // için createdAt referans alınır
          if (!last && a.createdAt) {
            last = new Date(a.createdAt).getTime();
          }
          if (now.getTime() - last >= intervalMs) {
            if (memFiredRef.current.has(memK + "_" + last)) continue;
            playAudio(a);
            memFiredRef.current.add(memK + "_" + last);
            try {
              localStorage.setItem(k, String(now.getTime()));
            } catch (_) {}
          }
        }
      }
    };

    // Hemen bir kez kontrol et, sonra periyodik
    tick();
    const id = setInterval(tick, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [organization]);
};

export default useV46AlarmScheduler;
